import { Images } from '@/assets';
import { fonts } from '@/theme/fonts';
import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import DashBoard from './dashboard/DashBoard';
import { useAppSelector } from '@/stores/hooks';
import { useLoading } from '@/context/LoadingContext';
import { getPartnerBusinessProfile } from '@/api/business-profile/getPartnerBusinessProfile';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { useDispatch } from 'react-redux';
import { useAlert } from '@/hooks/useAlert';
import { useNavigate } from 'react-router-dom';
import { langPath } from '@/utils/langPath';
import { auth } from '@/stores/slices/authSlices';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { getPartnerMe } from '@/api/profile/getPartnerMe';
import { getPartnerServiceOptions } from '@/api/service-regist/getPartnerServiceOptions';
import { service } from '@/stores/slices/service-regist/serviceSlices';

const MainPage = () => {
  const {
    shouldCheckBusinessProfile: checkProfile,
    shouldCheckServiceAdd: checkService,
  } = useAppSelector((state) => state.auth);
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { profileResponse: profile } = useAppSelector((state) => state.auth);

  const fetchBusinessProifle = useCallback(async () => {
    startLoading();
    try {
      const res = await getPartnerBusinessProfile();
      if (res.isSuccess) {
        dispatch(businessProfile.setBusinessProfile(res.data));
        const { languages } = res.data; // 비즈니스 프로필 모두 필수사항(시간, 요일은 초기 기본 셋팅값 존재)
        const langNone = languages.length === 0;
        if (langNone) {
          showAlert({
            message: t('yourBusinessProfileHasNotBeenSetUpYet'),
            onConfirm: () => navigate(langPath('/business-profile')),
            buttonText: t('goTo'),
            xButton: true,
          });
        }
      }
      dispatch(auth.setShouldCheckBusinessProfile(false));
    } finally {
      stopLoading();
    }
  }, [dispatch, navigate, showAlert, startLoading, stopLoading, t]);

  const fetchPartnerServiceOptions = useCallback(async () => {
    startLoading();
    try {
      const res = await getPartnerServiceOptions();
      if (res.isSuccess) {
        dispatch(service.setPartnerServiceOptions(res.data));
        const hasActiveService = res.data.some((service) => service.active);
        if (!hasActiveService) {
          showAlert({
            message: t('noRegisteredServices'),
            onConfirm: () => navigate(langPath('/service-registration')),
            buttonText: t('goTo'),
            xButton: true,
          });
        }
      }
      dispatch(auth.setShouldCheckServiceAdd(false));
    } finally {
      stopLoading();
    }
  }, [dispatch, navigate, showAlert, startLoading, stopLoading, t]);

  useEffect(() => {
    if (!checkProfile || !checkService) return;
    fetchBusinessProifle();
    fetchPartnerServiceOptions();
  }, [
    checkProfile,
    checkService,
    fetchBusinessProifle,
    fetchPartnerServiceOptions,
  ]);

  const fetchProfile = useCallback(async () => {
    startLoading();
    try {
      const res = await getPartnerMe();
      if (res.isSuccess) {
        dispatch(auth.setProfileMe(res.data));
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, startLoading, stopLoading]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  if (!profile) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Partner home</title>
      </Helmet>
      <Container>
        <ProfileSection>
          <ProfileImg
            src={`${import.meta.env.VITE_FILE_API_BASE_URL}/resource/files/${profile.profileImageId}`}
            alt="layout-nav-icon-profile"
          />
          <NameWrap>
            <Name>{profile.partnerName}</Name>
            <Email>{profile.loginId}</Email>
          </NameWrap>
        </ProfileSection>
        <MainSection>
          <ImgSection>
            <MainImg src={Images.MainImg} alt="main-img" />
            <TextWrap>
              <Welcome>Welcome</Welcome>
              <Desc>
                Ready, Set, Create!
                <br />
                {t('theR2PlayersFromAllAroundTheWorldAreWaitingForYou')}
              </Desc>
              <PointImg1 src={Images.MainPointImg1} alt="main-point-1" />
            </TextWrap>
          </ImgSection>
          <DashBoard />
        </MainSection>
      </Container>
    </>
  );
};

export default MainPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 99px;
  flex: 1;
  padding: 43px 13px;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding-bottom: 17px;
  border-bottom: 0.5px solid #89a1f4;
  height: 62px;
  box-sizing: border-box;
`;

const ProfileImg = styled.img`
  width: 45px;
  height: 45px;
  border-radius: 50%;
`;

const NameWrap = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  font-family: ${fonts.family.poppins};
`;

const Name = styled.div`
  font-weight: ${fonts.weight.bold};
  font-size: 20px;
  line-height: 120%;
`;

const Email = styled.div`
  font-size: 16px;
  line-height: 120%;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 104px;
`;

const ImgSection = styled.div`
  display: flex;
  padding-left: 28px;
  gap: 85px;
`;

const MainImg = styled.img`
  width: 183px;
  height: 230px;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #151131;
  text-align: left;
  justify-content: center;
  line-height: normal;
  position: relative;
`;

const Welcome = styled.div`
  font-weight: ${fonts.weight.bold};
  font-size: 40px;
`;

const Desc = styled.div`
  font-weight: ${fonts.weight.extraLight};
  font-size: 20px;
`;

const PointImg1 = styled.img`
  position: absolute;
  /* right: -26px; */
  left: 320px;
  bottom: 99px;
  width: 93.56px;
  height: 75.59px;
`;
